import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEMO_ORIGIN, demoUrl, warmDemo } from '../demo.ts';

/**
 * The demo, opened over this page.
 *
 * Every other panel on this site is a picture of the product drawn from a real
 * recording. This is the product. It arrives as a cross-origin iframe of the
 * stage host, which is the same thing a customer embeds on their own page — we
 * are our own first customer, and if the open feels wrong here it will feel
 * wrong there.
 *
 * ## Why it opens the way it does
 *
 * A frame pointed at another origin takes as long as it takes: a clone is a few
 * megabytes of somebody's real page. The usual answer is a spinner on a black
 * rectangle, and a black rectangle is how a fast product comes to feel slow,
 * because the first thing the visitor is shown is an absence.
 *
 * So nothing here waits on the network to start moving. The press opens an
 * aperture *from the thing that was clicked* — a `clip-path` inset animated to
 * full bleed, which grows the composition without distorting it the way a
 * scaled FLIP would — and behind that aperture is a still frame of the guide
 * about to speak, composed to sit exactly where the live curtain will. When the
 * frame posts `ready` the still crosses to the live one and the orb does not
 * move. The visitor sees one object opening, not a load.
 *
 * The reveal is gated on that message and never on a timer: `ready` is posted
 * after the clone is on screen and the curtain is up, so it means the thing
 * behind the still is genuinely there.
 */

type OpenArgs = { purpose?: string; ask?: string; from?: HTMLElement | null };
type Api = { open(args?: OpenArgs): void; warm(): void; live: boolean };

const Ctx = createContext<Api>({ open() {}, warm() {}, live: false });
export const useDemo = (): Api => useContext(Ctx);

type Phase = 'shut' | 'opening' | 'closing';

/* The aperture, as clip-path insets. Full bleed is four zeros. */
const SHUT_RADIUS = 11;
const apertureOf = (el: HTMLElement | null): string => {
  const r = el?.getBoundingClientRect();
  /*
    An origin off the viewport is no origin at all.

    A launcher can sit below the fold — the embed loader's default button lands
    in the footer — and a rect 11,000px down clamps to an inset with nothing
    inside it, so the aperture opens from a rectangle of zero area and the whole
    effect degenerates into an abrupt cut. Anything not actually on screen opens
    from the middle instead.
  */
  const onScreen = !!r && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  const box = onScreen && r
    ? r
    : { top: innerHeight / 2, right: innerWidth / 2, bottom: innerHeight / 2, left: innerWidth / 2 };
  // Pixels at both ends: `inset()` only interpolates cleanly when both are
  // written the same way, and a percentage start against a pixel end is where
  // this kind of animation silently stops animating in one browser.
  const px = (n: number, of: number) => `${Math.round(Math.min(Math.max(0, n), of))}px`;
  return [px(box.top, innerHeight), px(innerWidth - box.right, innerWidth),
    px(innerHeight - box.bottom, innerHeight), px(box.left, innerWidth)].join(' ');
};

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<Phase>('shut');
  const [wide, setWide] = useState(false);
  const [ready, setReady] = useState(false);
  const [stall, setStall] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [src, setSrc] = useState('');
  const [aperture, setAperture] = useState('');

  const frame = useRef<HTMLIFrameElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const origin = useRef<HTMLElement | null>(null);
  const timers = useRef<number[]>([]);

  const after = (ms: number, fn: () => void) => { timers.current.push(window.setTimeout(fn, ms)); };
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const close = useCallback(() => {
    setPhase((p) => {
      if (p !== 'opening') return p;
      clearTimers();
      /*
        Say so before removing the frame. Pulling an iframe out of the document
        does not reliably fire `pagehide`, and an abandoned conversation that is
        never closed is a recording the client is later shown as a session that
        simply stopped.
      */
      frame.current?.contentWindow?.postMessage({ source: 'persivo', type: 'close' }, DEMO_ORIGIN);
      setWide(false);
      after(460, () => {
        setPhase('shut'); setSrc(''); setReady(false); setStall(0); setLoaded(false);
        document.documentElement.classList.remove('demo-open');
        origin.current?.focus?.();
      });
      return 'closing';
    });
  }, []);

  const open = useCallback(({ purpose, ask, from }: OpenArgs = {}) => {
    clearTimers();
    origin.current = from ?? null;
    setAperture(apertureOf(from ?? null));
    setSrc(demoUrl(purpose, ask));
    setReady(false); setStall(0); setLoaded(false); setWide(false);
    setPhase('opening');
    document.documentElement.classList.add('demo-open');
    // One frame to let the aperture paint at its starting size, then widen it.
    requestAnimationFrame(() => requestAnimationFrame(() => setWide(true)));
    /*
      Waiting, in two honest stages — and neither of them apologises.

      The first version of this said "this is taking longer than it should"
      after fourteen seconds and offered a new tab. Both halves were wrong. We
      do not know what it should take, and saying so tells a buyer our product
      is broken at the exact moment we are asking them to trust it; and a new
      tab re-downloads the same clone over the same connection, so in the one
      case it is meant for — slow — it is strictly worse than waiting.

      So: at ten seconds, say what is coming rather than what is wrong. A
      visitor told they are a moment away from clicking through the product
      reads the delay as anticipation; a visitor told the product is late reads
      it as a fault.

      At twenty-six seconds it is no longer slow, it is wrong — and the likeliest
      cause of a cross-origin frame that never boots is something refusing to
      let it. That is the one case a new tab genuinely fixes, so that is the
      only place it is offered, named for what it is rather than dressed as an
      apology.
    */
    after(10000, () => setStall((n) => Math.max(n, 1)));
    after(26000, () => setStall(2));
  }, []);

  /* The three words the stage says to whoever framed it. */
  useEffect(() => {
    if (phase !== 'opening' || new URLSearchParams(location.search).get('recording') !== 'persivo') return;
    const forward = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.altKey || event.metaKey) return;
      if ((event.target as HTMLElement)?.closest('input,textarea,[contenteditable=true]')) return;
      const key = event.key.toLowerCase();
      if (!['n', 'p', 'r', 'h'].includes(key)) return;
      event.preventDefault();
      frame.current?.contentWindow?.postMessage({
        source: 'persivo', type: 'recording-command', command: key === 'r' && event.shiftKey ? 'restart' : key,
      }, DEMO_ORIGIN);
    };
    window.addEventListener('keydown', forward);
    return () => window.removeEventListener('keydown', forward);
  }, [phase]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== DEMO_ORIGIN) return;
      const data = e.data as { source?: string; type?: string } | null;
      if (!data || data.source !== 'persivo') return;
      if (data.type === 'ready') { setReady(true); setStall(0); }
      // Let the closing line land before the page comes back.
      if (data.type === 'ended') after(1100, close);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [close]);

  useEffect(() => {
    if (phase === 'shut') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, close]);

  // Focus follows the reveal, not the open: moving focus into the overlay while
  // it is still a picture would strand a keyboard user on nothing.
  useEffect(() => { if (ready) closeBtn.current?.focus(); }, [ready]);

  useEffect(() => clearTimers, []);

  const overlay = phase === 'shut' ? null : createPortal(
    <div className={`dm${wide ? ' wide' : ''}${ready ? ' ready' : ''}${stall === 2 ? ' stalled' : ''}`}
      role="dialog" aria-modal="true"
      aria-label="Interactive product demo" data-ground="stage"
      style={{ '--dm-aperture': aperture } as React.CSSProperties}>

      {/*
        The still. It is not a placeholder for the demo — it is the first frame
        of it, composed where the live curtain lands so the cross-fade moves
        nothing.
      */}
      <div className="dm-still" aria-hidden={ready}>
        {/* The site's own orb, not a second one. It is the object the visitor
            pressed and the object the live curtain opens with, so across the
            cross-fade it does not move. */}
        <img className="dm-orb" src="/agent-thinking.svg" alt="" />
        {/*
          What they are getting, not what it isn't.

          This used to open with "Opening a copy of the product" over "It is a
          real page, frozen." Every word of that was true and all of it was the
          wrong first thing to hand a buyer: the opening frame of a sales demo
          spent introducing the product as a lesser version of itself. The
          reassurance is real and it still gets said — on the curtain, one screen
          later, where it lands as permission to click rather than as a caveat
          about what they are being shown.
        */}
        <p className="dm-line">Your demo is opening</p>
        <p className="dm-sub">Your guide is getting the product ready.</p>
        <div className="dm-bar"><i /></div>
        {stall === 1 && (
          <p className="dm-slow">Almost there — in a moment you will be able to click through the
            product yourself.</p>
        )}
        {/* No "go back to the page" — the ✕ is in the corner and Escape works.
            A third way out of the same room is noise, and phrasing it as a
            retreat invites one. */}
        {stall === 2 && (
          <p className="dm-slow">The demo is unavailable here right now.{' '}
            <a href={src} target="_blank" rel="noreferrer">Open it in its own tab →</a></p>
        )}
      </div>

      {src && (
        <iframe ref={frame} className="dm-frame" src={src} title="Persivo — interactive product demo"
          allow="microphone" onLoad={() => setLoaded(true)} />
      )}

      <button ref={closeBtn} type="button" className="dm-x" onClick={close}
        title="Close the demo (Esc)" aria-label="Close the demo">
        <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true">
          <path d="M2 2l11 11M13 2L2 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>,
    document.body,
  );

  /*
    The page behind is dimmed by a class on the document, not by a wrapper.
    Wrapping the site in a transformed div would have made that div the
    containing block for everything fixed inside it and changed what three
    sticky columns stick to — a large, silent regression to buy an effect that
    is only visible for the first fifth of a second.
  */
  return (
    <Ctx.Provider value={{ open, warm: warmDemo, live: phase !== 'shut' }}>
      {children}

      {overlay}
    </Ctx.Provider>
  );
}


